import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { getPack } from "@/lib/offline/packs";
import { enqueue } from "@/lib/offline/outbox";
import { newId, saveProgress } from "@/lib/offline/offline-progress";
import { celebrate } from "@/components/makai/celebration";
import { useServerFn } from "@tanstack/react-start";
import { gradePaperAnswer, remediateMissedQuestion } from "@/lib/past-papers.functions";
import { recordSignals } from "@/lib/intelligence.functions";
import { generateReportCard } from "@/lib/report-card.functions";
import {
  Loader2, Timer, ArrowLeft, ArrowRight, Send, CheckCircle2, XCircle,
  Trophy, AlertCircle, Sparkles, BookOpen, Lightbulb, ChevronDown, ChevronUp, GraduationCap,
} from "lucide-react";
import {
  SmartWorking,
  parseSmartWorking,
  formatSmartWorkingText,
  emptySmartWorking,
  type SmartWorkingValue,
} from "@/components/paper/smart-working";

export const Route = createFileRoute("/learner/papers/$paperId")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({ meta: [{ title: "Past Paper — MAKAI" }] }),
  component: PaperPlayer,
});

type Paper = {
  id: string;
  title: string;
  subject: string;
  level: string;
  exam_body: string;
  year: number | null;
  description: string | null;
  instructions: string | null;
  total_marks: number;
  pass_mark: number;
  time_limit_min: number | null;
};

type QType = "mcq" | "true_false" | "short" | "fill_blank" | "essay";

type PQ = {
  id: string;
  sort_order: number;
  type: QType;
  stem: string;
  options: string[] | null;
  correct_answer: string | null;
  accepted_answers: string[] | null;
  explanation: string | null;
  marks: number;
  topic: string | null;
  marking_guide: string | null;
};

type Answer = string | string[] | null;

type GradedDetail = {
  question_id: string;
  answer: Answer;
  score: number;
  max: number;
  is_correct: boolean | null; // null = ai-graded partial
  feedback?: string;
};

function norm(s: string) {
  return s.trim().toLowerCase().replace(/\s+/g, " ").replace(/[.,]/g, "");
}

function slug(s: string) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "unknown";
}

function gradeLocal(q: PQ, ans: Answer): { score: number; isCorrect: boolean | null; needsAi: boolean } {
  if (ans == null || ans === "" || (Array.isArray(ans) && ans.every((x) => !x))) {
    return { score: 0, isCorrect: false, needsAi: false };
  }
  if (q.type === "mcq") {
    const ok = String(ans) === String(q.correct_answer);
    return { score: ok ? q.marks : 0, isCorrect: ok, needsAi: false };
  }
  if (q.type === "true_false") {
    const ok = String(ans).toLowerCase() === String(q.correct_answer ?? "").toLowerCase();
    return { score: ok ? q.marks : 0, isCorrect: ok, needsAi: false };
  }
  if (q.type === "fill_blank") {
    let blanks: string[] = [];
    try { blanks = JSON.parse(q.correct_answer ?? "[]"); } catch { blanks = [String(q.correct_answer ?? "")]; }
    const given = Array.isArray(ans) ? ans : [String(ans)];
    const total = blanks.length || 1;
    let right = 0;
    for (let i = 0; i < total; i++) {
      const accepted = [String(blanks[i] ?? ""), ...(q.accepted_answers ?? [])].map(norm);
      if (accepted.includes(norm(String(given[i] ?? "")))) right++;
    }
    const ratio = right / total;
    return { score: +(q.marks * ratio).toFixed(2), isCorrect: ratio === 1, needsAi: false };
  }
  if (q.type === "short") {
    const canon = [q.correct_answer ?? "", ...(q.accepted_answers ?? [])].map(norm);
    const ok = canon.includes(norm(String(ans)));
    return { score: ok ? q.marks : 0, isCorrect: ok, needsAi: !ok };
  }
  // essay always needs AI
  return { score: 0, isCorrect: null, needsAi: true };
}

function PaperPlayer() {
  const { paperId } = Route.useParams();
  const navigate = useNavigate();
  const gradeAi = useServerFn(gradePaperAnswer);
  const remediate = useServerFn(remediateMissedQuestion);
  const sendSignals = useServerFn(recordSignals);
  const genReport = useServerFn(generateReportCard);
  const [reportId, setReportId] = useState<string | null>(null);

  const [paper, setPaper] = useState<Paper | null>(null);
  const [questions, setQuestions] = useState<PQ[]>([]);
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [idx, setIdx] = useState(0);
  const [started, setStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [remaining, setRemaining] = useState<number | null>(null);
  const startedAt = useRef<number>(0);
  const [result, setResult] = useState<{
    percent: number; total: number; score: number; passed: boolean;
    detail: GradedDetail[]; needsGrading: boolean;
  } | null>(null);
  const [learnerId, setLearnerId] = useState<string | null>(null);
  const [offlineMode, setOfflineMode] = useState(false);

  useEffect(() => {
    (async () => {
      let p: Paper | null = null;
      let qs: PQ[] = [];
      if (navigator.onLine) {
        const [{ data: pRow }, { data: qRows }] = await Promise.all([
          supabase.from("past_papers").select("*").eq("id", paperId).eq("published", true).maybeSingle(),
          supabase.from("past_paper_questions").select("*").eq("paper_id", paperId).order("sort_order"),
        ]);
        p = (pRow as Paper) ?? null;
        qs = (qRows ?? []) as PQ[];
      }
      if (!p) {
        // Offline (or blocked) — fall back to the downloaded pack.
        const pack = await getPack("paper", paperId);
        if (pack) {
          p = pack.data.paper as Paper;
          qs = (pack.data.questions ?? []) as PQ[];
          setOfflineMode(true);
        }
      }
      if (!p) { toast.error("Paper not available offline. Download it first."); navigate({ to: "/learner/library" }); return; }
      setPaper(p);
      setQuestions(qs);
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: l } = await supabase.from("learners").select("id").eq("user_id", user.id).maybeSingle();
        setLearnerId(l?.id ?? null);
      }
    })();
  }, [paperId, navigate]);

  function begin() {
    if (!paper) return;
    setStarted(true);
    startedAt.current = Date.now();
    if (paper.time_limit_min) setRemaining(paper.time_limit_min * 60);
  }

  useEffect(() => {
    if (!started || remaining == null || result) return;
    if (remaining <= 0) { submit(); return; }
    const t = setTimeout(() => setRemaining((r) => (r ?? 0) - 1), 1000);
    return () => clearTimeout(t);
  }, [started, remaining, result]); // eslint-disable-line react-hooks/exhaustive-deps

  async function submit() {
    if (!paper || submitting || result) return;
    setSubmitting(true);
    const detail: GradedDetail[] = [];
    let totalScore = 0;
    let needsGrading = false;
    for (const q of questions) {
      const ans = answers[q.id] ?? null;
      const g = gradeLocal(q, ans);
      let score = g.score;
      let isCorrect: boolean | null = g.isCorrect;
      let feedback: string | undefined;
      if (g.needsAi && !navigator.onLine) {
        // Offline: keep the answer for a teacher/AI to mark once we reconnect.
        needsGrading = true;
      } else if (g.needsAi && ans != null && String(ans).trim().length > 0) {
        try {
          let learnerAnswer: string;
          if (Array.isArray(ans)) learnerAnswer = ans.join(" | ");
          else if (typeof ans === "string" && ans.trim().startsWith("{")) {
            try { learnerAnswer = formatSmartWorkingText(parseSmartWorking(ans)) || ans; }
            catch { learnerAnswer = ans; }
          } else learnerAnswer = String(ans);
          const r = await gradeAi({
            data: {
              stem: q.stem,
              learnerAnswer,
              markingGuide: q.marking_guide,
              modelAnswer: q.explanation,
              maxMarks: q.marks,
            },
          });
          score = r.score;
          isCorrect = r.score >= q.marks ? true : r.score > 0 ? null : false;
          feedback = r.feedback;
          if (r.score < q.marks) needsGrading = false; // fully auto-marked by AI
        } catch {
          needsGrading = true;
        }
      }
      totalScore += score;
      detail.push({ question_id: q.id, answer: ans, score, max: q.marks, is_correct: isCorrect, feedback });
    }
    const total = paper.total_marks || questions.reduce((s, q) => s + q.marks, 0) || 1;
    const percent = +((totalScore / total) * 100).toFixed(2);
    const passed = percent >= paper.pass_mark;
    const timeSpent = Math.round((Date.now() - startedAt.current) / 1000);
    const { data: { user } } = await supabase.auth.getUser();
    const attemptRow = {
      id: newId(),
      paper_id: paperId, user_id: user?.id, learner_id: learnerId,
      started_at: new Date(startedAt.current).toISOString(),
      submitted_at: new Date().toISOString(),
      score: totalScore, total, percent, passed,
      answers: detail, needs_grading: needsGrading,
      time_spent_sec: timeSpent,
    };
    if (user) {
      if (navigator.onLine) {
        const { error } = await supabase.from("past_paper_attempts").insert(attemptRow as never);
        if (error) {
          await enqueue({ op: "insert", table: "past_paper_attempts", payload: attemptRow, user_id: user.id, dedupeKey: `paper_attempt:${attemptRow.id}` });
        }
      } else {
        // Offline: queue the attempt; it uploads itself on reconnect.
        await enqueue({ op: "insert", table: "past_paper_attempts", payload: attemptRow, user_id: user.id, dedupeKey: `paper_attempt:${attemptRow.id}` });
        await saveProgress(`paper:${paperId}`, { completed: true, score: totalScore, total });
        toast.success("Saved on this device — it will send when you're back online.");
      }
    }
    // Feed the Learner Intelligence engine — one signal per graded question.
    try {
      const subjectKey = slug(paper.subject);
      const signals = questions.map((q, i) => {
        const d = detail[i];
        const outcome: "correct" | "partial" | "wrong" | "skipped" =
          d.answer == null || d.answer === "" ? "skipped"
          : d.is_correct === true ? "correct"
          : d.score > 0 ? "partial"
          : "wrong";
        const topicLabel = q.topic ?? "General";
        const topicKey = `${subjectKey}:${slug(topicLabel)}`;
        const nodeKey = `${topicKey}:q-${q.id}`;
        const errorKind: "unknown" | "conceptual" | "careless" | null =
          outcome === "correct" ? null : (q.type === "essay" || q.type === "short") ? "conceptual" : "unknown";
        return {
          source: "past_paper" as const,
          sourceRef: q.id,
          nodeKey,
          nodeLabel: (q.stem ?? "Question").slice(0, 80),
          level: "concept" as const,
          subject: paper.subject,
          ancestors: [
            { key: subjectKey, label: paper.subject, level: "subject" as const },
            { key: topicKey, label: topicLabel, level: "topic" as const },
          ],
          outcome,
          errorKind,
          difficulty: Math.min(1, q.marks / 5),
          weight: Math.max(1, Math.min(3, q.marks / 2)),
          meta: { paperId, questionType: q.type },
        };
      });
      sendSignals({ data: { signals } }).catch(() => {});
    } catch (e) {
      console.error("[intelligence] paper submit", e);
    }
    setResult({ percent, total, score: totalScore, passed, detail, needsGrading });
    celebrate({
      type: passed ? "level" : "xp",
      title: passed ? `Paper passed · ${percent}%` : `Paper submitted · ${percent}%`,
      message: passed ? "Great work — check the review below." : "Let's turn misses into wins.",
    });
    setSubmitting(false);

    // Generate AI Report Card (best-effort)
    if (learnerId) {
      try {
        const res = await genReport({
          data: {
            learnerId,
            sourceType: "paper",
            sourceRef: paperId,
            title: paper.title ?? "Past paper",
            subject: paper.subject ?? null,
            totalMarks: total,
            score: totalScore,
            timeTakenSec: timeSpent,
            attempted: detail.filter((d) => d.answer != null && d.answer !== "").length,
            totalQuestions: questions.length,
            questions: questions.map((qq, i) => {
              const d = detail[i];
              return {
                prompt: qq.stem,
                topic: qq.topic ?? paper.subject,
                correct: d.is_correct,
                skipped: d.answer == null || d.answer === "",
                marks: qq.marks,
              };
            }),
          },
        });
        setReportId(res.id);
      } catch (err) { console.warn("Report generation failed", err); }
    }
  }

  if (!paper) {
    return (
      <AppShell role="learner" title="Past paper">
        <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      </AppShell>
    );
  }

  const left = remaining != null
    ? `${Math.floor(remaining / 60).toString().padStart(2, "0")}:${(remaining % 60).toString().padStart(2, "0")}`
    : null;

  if (result) return <ResultScreen paper={paper} questions={questions} result={result} remediate={remediate} reportId={reportId} onRetry={() => { setResult(null); setAnswers({}); setIdx(0); setStarted(false); }} />;

  if (!started) {
    return (
      <AppShell role="learner" title={paper.title} subtitle={`${paper.exam_body} · ${paper.subject} · ${paper.level}${paper.year ? ` · ${paper.year}` : ""}`}>
        <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary">
            <GraduationCap className="h-4 w-4" /> Interactive past paper
          </div>
          {paper.description && <p className="mt-3 text-sm text-muted-foreground">{paper.description}</p>}
          {paper.instructions && (
            <div className="mt-4 rounded-xl bg-muted/40 p-4 text-sm whitespace-pre-wrap leading-relaxed">{paper.instructions}</div>
          )}
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Questions" value={questions.length} />
            <Stat label="Total marks" value={paper.total_marks} />
            <Stat label="Pass mark" value={`${paper.pass_mark}%`} />
            {paper.time_limit_min && <Stat label="Time" value={`${paper.time_limit_min} min`} />}
          </div>
          <Button className="mt-6 w-full" size="lg" onClick={begin} disabled={questions.length === 0}>
            Start paper
          </Button>
          <p className="mt-3 text-center text-xs text-muted-foreground">
            We auto-mark your answers and take you into a mini-lesson for anything you miss.
          </p>
        </div>
      </AppShell>
    );
  }

  const q = questions[idx];
  const answered = questions.filter((qq) => {
    const a = answers[qq.id];
    return a != null && a !== "" && !(Array.isArray(a) && a.every((x) => !x));
  }).length;

  return (
    <AppShell
      role="learner"
      title={paper.title}
      subtitle={`Question ${idx + 1} of ${questions.length}`}
      action={left ? (
        <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-bold ${remaining! < 60 ? "bg-clay/20 text-clay-foreground" : "bg-primary/10 text-primary"}`}>
          <Timer className="h-4 w-4" /> {left}
        </span>
      ) : undefined}
    >
      <div className="mb-3 h-1.5 overflow-hidden rounded-full bg-muted">
        <div className="h-full bg-primary transition-all" style={{ width: `${((idx + 1) / questions.length) * 100}%` }} />
      </div>
      <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
          {typeLabel(q.type)} · {q.marks} {q.marks === 1 ? "mark" : "marks"}{q.topic ? ` · ${q.topic}` : ""}
        </div>
        <h3 className="mt-1 whitespace-pre-wrap font-display text-lg font-bold leading-tight">{q.stem}</h3>
        <div className="mt-4">
          <QuestionInput q={q} subject={paper.subject} value={answers[q.id]} onChange={(v) => setAnswers({ ...answers, [q.id]: v })} />
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button variant="outline" disabled={idx === 0} onClick={() => setIdx(idx - 1)}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Previous
        </Button>
        {idx < questions.length - 1 ? (
          <Button onClick={() => setIdx(idx + 1)}>Next <ArrowRight className="ml-1 h-4 w-4" /></Button>
        ) : (
          <Button onClick={submit} disabled={submitting}>
            {submitting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
            {submitting ? "Marking…" : "Submit paper"}
          </Button>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {questions.map((qq, i) => {
          const has = answers[qq.id] != null && answers[qq.id] !== "";
          return (
            <button key={qq.id} onClick={() => setIdx(i)}
              className={`h-8 w-8 rounded-md text-xs font-bold ${i === idx ? "bg-primary text-primary-foreground" : has ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
              {i + 1}
            </button>
          );
        })}
      </div>
      <div className="mt-2 text-center text-xs text-muted-foreground">{answered}/{questions.length} answered</div>
    </AppShell>
  );
}

function typeLabel(t: QType) {
  return {
    mcq: "Multiple choice",
    true_false: "True / False",
    short: "Short answer",
    fill_blank: "Fill in the blank",
    essay: "Long answer",
  }[t];
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl bg-muted/40 p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-display text-lg font-bold capitalize">{value}</div>
    </div>
  );
}

function QuestionInput({ q, subject, value, onChange }: { q: PQ; subject: string; value: Answer | undefined; onChange: (v: Answer) => void }) {
  if (q.type === "mcq") {
    const opts = Array.isArray(q.options) ? q.options : [];
    return (
      <div className="space-y-2">
        {opts.map((o, i) => (
          <label key={i} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 ${String(value) === String(i) ? "border-primary bg-primary/5" : "border-border"}`}>
            <input type="radio" checked={String(value) === String(i)} onChange={() => onChange(String(i))} className="accent-primary" />
            <span className="text-sm">{o}</span>
          </label>
        ))}
      </div>
    );
  }
  if (q.type === "true_false") {
    return (
      <div className="grid grid-cols-2 gap-2">
        {["true", "false"].map((v) => (
          <button key={v} type="button" onClick={() => onChange(v)}
            className={`rounded-xl border p-4 text-sm font-bold capitalize ${value === v ? "border-primary bg-primary/10 text-primary" : "border-border"}`}>
            {v}
          </button>
        ))}
      </div>
    );
  }
  if (q.type === "fill_blank") {
    let blanks: string[] = [];
    try { blanks = JSON.parse(q.correct_answer ?? "[]"); } catch { blanks = [""]; }
    const arr: string[] = Array.isArray(value) ? value : new Array(blanks.length).fill("");
    return (
      <div className="space-y-2">
        {blanks.map((_, i) => (
          <Input key={i} placeholder={`Blank ${i + 1}`} value={arr[i] ?? ""}
            onChange={(e) => { const next = [...arr]; next[i] = e.target.value; onChange(next); }} />
        ))}
      </div>
    );
  }
  if (q.type === "short") {
    return <Input placeholder="Type your answer" value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value)} />;
  }
  // Essay: Mathematics gets Smart Working, other subjects keep the plain textarea.
  const isMath = /math/i.test(subject);
  if (isMath) {
    const sw: SmartWorkingValue = parseSmartWorking((value as string) ?? null);
    return (
      <SmartWorking
        stem={q.stem}
        value={sw}
        onChange={(next) => onChange(JSON.stringify(next))}
      />
    );
  }
  return (
    <Textarea
      rows={6}
      placeholder="Show your working and give your final answer."
      value={(value as string) ?? ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/* ---------------- Result screen with remediation ---------------- */

function ResultScreen({
  paper, questions, result, onRetry, remediate, reportId,
}: {
  paper: Paper;
  questions: PQ[];
  result: NonNullable<ReturnType<typeof useState<{ percent: number; total: number; score: number; passed: boolean; detail: GradedDetail[]; needsGrading: boolean }>>[0]>;
  onRetry: () => void;
  remediate: (opts: { data: { stem: string; topic: string | null; subject: string; correctAnswer?: string | null; explanation?: string | null; learnerAnswer?: string | null } }) => Promise<{ miniLesson: string; lessons: { id: string; title: string; topic: string | null; duration_min: number | null }[] }>;
  reportId: string | null;
}) {
  const missed = useMemo(
    () => result.detail.filter((d) => d.is_correct !== true),
    [result.detail],
  );
  return (
    <AppShell role="learner" title={paper.title} subtitle="Your results">
      <div className="rounded-3xl border border-border bg-gradient-to-br from-primary/10 via-card to-gold/10 p-6 text-center shadow-soft">
        <Trophy className={`mx-auto h-12 w-12 ${result.passed ? "text-gold" : "text-muted-foreground"}`} />
        <div className="mt-3 text-4xl font-display font-black">{result.percent}%</div>
        <div className="text-sm text-muted-foreground">{result.score} / {result.total} marks</div>
        <div className={`mt-2 inline-block rounded-full px-3 py-1 text-xs font-bold ${result.passed ? "bg-primary/15 text-primary" : "bg-clay/20 text-clay-foreground"}`}>
          {result.passed ? "Passed" : "Keep practising"}
        </div>
        {result.needsGrading && (
          <p className="mt-3 inline-flex items-center gap-1 text-xs text-muted-foreground">
            <AlertCircle className="h-3 w-3" /> Some long-answer parts still awaiting teacher review.
          </p>
        )}
        {reportId && (
          <div className="mt-4">
            <Link to="/learner/reports/$reportId" params={{ reportId }} className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
              <Sparkles className="h-4 w-4" /> View AI Report Card
            </Link>
          </div>
        )}
      </div>

      {missed.length > 0 && (
        <div className="mt-6 rounded-2xl border border-primary/30 bg-primary/5 p-4">
          <div className="flex items-center gap-2 font-bold">
            <Lightbulb className="h-5 w-5 text-primary" />
            Learn what you missed — {missed.length} target lesson{missed.length === 1 ? "" : "s"}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            MAKAI AI has prepared a short lesson for every question you didn't fully get. Tap to expand.
          </p>
        </div>
      )}

      <div className="mt-4 space-y-3">
        {questions.map((q, i) => {
          const d = result.detail.find((x) => x.question_id === q.id);
          if (!d) return null;
          return (
            <QuestionReview
              key={q.id}
              index={i}
              paper={paper}
              q={q}
              d={d}
              remediate={remediate}
            />
          );
        })}
      </div>

      <div className="mt-6 flex flex-col gap-2 sm:flex-row">
        <Button asChild variant="outline" className="flex-1">
          <Link to="/learner/library">Back to library</Link>
        </Button>
        <Button className="flex-1" onClick={onRetry}>Retry paper</Button>
      </div>
    </AppShell>
  );
}

function formatAnswer(q: PQ, ans: Answer): string {
  if (ans == null || ans === "") return "—";
  if (q.type === "mcq") {
    const i = Number(ans);
    return Array.isArray(q.options) ? (q.options[i] ?? String(ans)) : String(ans);
  }
  if (Array.isArray(ans)) return ans.join(", ");
  if (typeof ans === "string" && ans.trim().startsWith("{")) {
    try {
      const sw = parseSmartWorking(ans);
      const text = formatSmartWorkingText(sw);
      return text || "—";
    } catch { /* fall through */ }
  }
  return String(ans);
}
function formatCorrect(q: PQ): string {
  if (q.type === "mcq") {
    const i = Number(q.correct_answer);
    return Array.isArray(q.options) ? (q.options[i] ?? "") : "";
  }
  if (q.type === "fill_blank") {
    try { return (JSON.parse(q.correct_answer ?? "[]") as string[]).join(", "); } catch { return q.correct_answer ?? ""; }
  }
  return q.correct_answer ?? "";
}

function QuestionReview({
  index, paper, q, d, remediate,
}: {
  index: number; paper: Paper; q: PQ; d: GradedDetail;
  remediate: (opts: { data: { stem: string; topic: string | null; subject: string; correctAnswer?: string | null; explanation?: string | null; learnerAnswer?: string | null } }) => Promise<{ miniLesson: string; lessons: { id: string; title: string; topic: string | null; duration_min: number | null }[] }>;
}) {
  const correct = d.is_correct === true;
  const [open, setOpen] = useState(false);
  const [rem, setRem] = useState<{ miniLesson: string; lessons: { id: string; title: string; topic: string | null; duration_min: number | null }[] } | null>(null);
  const [busy, setBusy] = useState(false);

  async function loadRemediation() {
    if (rem || busy || correct) { setOpen((o) => !o); return; }
    setOpen(true);
    setBusy(true);
    try {
      const r = await remediate({
        data: {
          stem: q.stem,
          topic: q.topic,
          subject: paper.subject,
          correctAnswer: formatCorrect(q),
          explanation: q.explanation,
          learnerAnswer: formatAnswer(q, d.answer),
        },
      });
      setRem(r);
    } catch (e) {
      toast.error("Could not load the mini-lesson — please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`rounded-2xl border ${correct ? "border-border" : "border-primary/40"} bg-card p-4`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            Q{index + 1} · {q.marks} {q.marks === 1 ? "mark" : "marks"}{q.topic ? ` · ${q.topic}` : ""}
          </div>
          <div className="mt-1 whitespace-pre-wrap text-sm font-semibold">{q.stem}</div>
        </div>
        {correct ? (
          <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" />
        ) : d.is_correct === null ? (
          <span className="rounded-full bg-gold/15 px-2 py-0.5 text-[10px] font-bold text-gold-foreground">Partial · {d.score}/{d.max}</span>
        ) : (
          <XCircle className="h-5 w-5 shrink-0 text-clay" />
        )}
      </div>
      <div className="mt-2 text-xs text-muted-foreground">
        Your answer: <span className="font-medium text-foreground">{formatAnswer(q, d.answer)}</span>
      </div>
      {!correct && formatCorrect(q) && (
        <div className="mt-1 text-xs text-muted-foreground">
          Correct: <span className="font-medium text-primary">{formatCorrect(q)}</span>
        </div>
      )}
      {d.feedback && (
        <div className="mt-2 rounded-lg bg-muted/50 p-2 text-xs">
          <span className="font-bold">Marker note:</span> {d.feedback}
        </div>
      )}
      {q.explanation && correct && (
        <div className="mt-2 rounded-lg bg-muted/50 p-2 text-xs">{q.explanation}</div>
      )}

      {!correct && (
        <div className="mt-3">
          <button
            onClick={loadRemediation}
            className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-bold text-primary hover:bg-primary/15"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {open ? "Hide mini-lesson" : "Learn this now"}
            {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
          {open && (
            <div className="mt-3 space-y-3 rounded-xl border border-primary/20 bg-primary/5 p-3">
              {busy && !rem ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" /> MAKAI AI is preparing your mini-lesson…
                </div>
              ) : rem ? (
                <>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">{rem.miniLesson}</p>
                  {rem.lessons.length > 0 && (
                    <div>
                      <div className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        <BookOpen className="h-3.5 w-3.5" /> Full lessons on this topic
                      </div>
                      <ul className="space-y-1">
                        {rem.lessons.map((l) => (
                          <li key={l.id}>
                            <Link
                              to="/learner/lessons" search={{ subject: undefined }}
                              className="inline-flex items-center gap-1 text-sm text-primary underline-offset-2 hover:underline"
                            >
                              {l.title}
                              {l.duration_min ? <span className="text-xs text-muted-foreground"> · {l.duration_min} min</span> : null}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
